import { Helmet } from 'react-helmet-async';
import { Route, Routes, useLocation } from 'react-router';

import '../initDayjs';

import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { usePageViewTracking } from './hooks/useAnalytics';
import { useButtonClickGuard } from './hooks/useButtonClickGuard';
import { useFcmToken } from './hooks/useFcmToken';
import deviceStore from './stores/deviceStore';
import firebaseStore from './stores/firebaseStore';
import historyStore from './stores/historyStore';
import themeStore from './stores/themeStore';
import webViewStore from './stores/webViewStore';
import DailyBriefPage from './views/brief/DailyBriefPage';
import IndexHoldingsPage from './views/brief/IndexHoldingsPage';
import SectorHoldingsPage from './views/brief/SectorHoldingsPage';
import ChallengePage from './views/challenge/ChallengePage';
import ChannelPage from './views/channel/ChannelPage';
import PostEditorPage from './views/channel/PostEditorPage';
import PostPage from './views/channel/PostPage';
import FeaturedStockPage from './views/featured/FeaturedStockPage';
import Home from './views/home/HomePage';
import Layout from './views/Layout';
import AccountDeletePage from './views/more/account/AccountDeletePage';
import LicensePage from './views/more/license/LicensePage';
import MorePage from './views/more/MorePage';
import NewsDetailPage from './views/news/NewsDetailPage';
import NewsListPage from './views/news/NewsListPage';
import NotificationPage from './views/notification/NotificationPage';
import PortfolioDetailPage from './views/portfolio/detail/PortfolioDetailPage';
import PortfolioListPage from './views/portfolio/PortfolioListPage';
import ReportDetailPage from './views/portfolio/report/ReportDetailPage';
import SecurityDetailPage from './views/portfolio/security/SecurityDetailPage';
import StockDetailPage from './views/stock/StockDetailPage';
import StrategyCreatePage from './views/strategy/StrategyCreatePage';
import StrategyDetailPage from './views/strategy/StrategyDetailPage';
import StrategyEditPage from './views/strategy/StrategyEditPage';
import TermsPage from './views/terms/TermsPage';
import BridgeTestPage from './views/test/BridgeTestPage';
import DeepLinkTestPage from './views/test/DeepLinkTestPage';
import ReadabilityTestPage from './views/test/ReadbilityTestPage';

const App = observer(() => {
    const location = useLocation();

    useEffect(() => {
        historyStore.push(location);
    }, [location.pathname]);

    useEffect(() => {
        deviceStore.init();
        webViewStore.initialize();
        themeStore.initialize();

        // 순서 중요: webViewStore.initialize() 가 먼저 호출되어야 함
        firebaseStore.initialize(webViewStore.isWebView);
    }, []);

    usePageViewTracking();
    useFcmToken();

    useButtonClickGuard();

    return (
        // @see React Router: https://reactrouter.com/start/library/routing
        <>
            <Helmet>
                <title>해와오 - 해외투자와 포트폴리오</title>
            </Helmet>
            <Routes>
                <Route element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path="/portfolio" element={<PortfolioListPage />} />
                    <Route path="/portfolio/:institutionSlug" element={<PortfolioDetailPage />} />
                    <Route
                        path="/portfolio/:institutionSlug/report/:reportYear/:reportQuarter"
                        element={<ReportDetailPage />}
                    />
                    <Route
                        path="/portfolio/:institutionSlug/security/:securityHash"
                        element={<SecurityDetailPage />}
                    />
                    <Route path="/news" element={<NewsListPage />} />
                    <Route path="/news/:newsSummarySlug" element={<NewsDetailPage />} />
                    <Route path="/featured" element={<FeaturedStockPage />} />
                    <Route path="/daily-brief" element={<DailyBriefPage />} />
                    <Route
                        path="/daily-brief/sector/:sectorSlug"
                        element={<SectorHoldingsPage />}
                    />
                    <Route path="/daily-brief/index/:indexTicker" element={<IndexHoldingsPage />} />
                    <Route path="/challenge" element={<ChallengePage />} />
                    <Route path="/strategy/create" element={<StrategyCreatePage />} />
                    <Route path="/strategy/:hash" element={<StrategyDetailPage />} />
                    <Route path="/strategy/:hash/edit" element={<StrategyEditPage />} />
                    <Route path="/more" element={<MorePage />} />
                    <Route path="/notification" element={<NotificationPage />} />
                    <Route path="/terms" element={<TermsPage type="terms" />} />
                    <Route path="/privacy" element={<TermsPage type="privacy" />} />
                    <Route path="/more/license" element={<LicensePage />} />
                    <Route path="/more/account-delete" element={<AccountDeletePage />} />
                    <Route path="/channel/:channelType" element={<ChannelPage />} />
                    <Route path="/channel/:channelType/:postHash" element={<PostPage />} />
                    <Route path="/channel/:channelType/editor" element={<PostEditorPage />} />
                    <Route path="/stock/:ticker" element={<StockDetailPage />} />
                    {import.meta.env.DEV && (
                        <>
                            <Route path="/bridge-test" element={<BridgeTestPage />} />
                            <Route path="/deeplink-test" element={<DeepLinkTestPage />} />
                            <Route path="/readability-test" element={<ReadabilityTestPage />} />
                        </>
                    )}
                </Route>
            </Routes>
        </>
    );
});

export default App;
